import React from 'react';

import type { AudioDropFileData } from './types';
import { cn } from '../cn';
import Button from '../Input2/Button';
import FilterSelect from '../Input2/FilterSelect';
import Textarea from '../Input2/Textarea';
import { StepIconSvg } from '../StepIcon';
import { Editor, General, Media } from '../StrokeIcons';
import { getTextFromHtml } from '../utils/htmlUtils';
import { VoiceRecorder, type VoiceRecorderProps } from '../VoiceRecorder';
import { WaveSpinner } from '../WaveSpinner';

// ============================================================================
// SeparatorWithPlusIcon
// ============================================================================

function SeparatorWithPlusIcon() {
  return (
    <div className="flex flex-col items-center relative z-10">
      <div className="flex items-center justify-center size-5 shrink-0 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full text-indigo-500 bg-indigo-100 dark:bg-indigo-900">
        <General.PlusCircle className="size-4" />
      </div>
      <div className="w-full h-px bg-indigo-300 dark:bg-indigo-700" />
    </div>
  );
}

// ============================================================================
// Types
// ============================================================================

export type VoiceNoteActionCardPureProps = {
  audioDropFiles: AudioDropFileData[];
  audioDropFilesIsLoading: boolean;
  onSendAudio: (fileId: string) => Promise<void>;
  onGenerateVoiceNote?: (
    text: string,
    voiceDropId?: string,
  ) => Promise<{ stepId: string; audioUrl: string; audioId: string } | undefined>;
  onApproveVoiceNote?: (stepId: string) => Promise<void>;
  /** Called when audio generation completes with URL so component can auto-play */
  onAudioGenerated?: (publicUrl: string, audioId: string) => void;
  onRecordTrigger?: () => void;
  /** Called when user records a voice note - handles upload to server and auto-play */
  onRecordingComplete?: (audioBlob: Blob, durationMs: number) => void;
  isUploadingRecording?: boolean;
  /** Generate AI draft text for the TTS message. Called by the magic-wand buttons
      (one on the collapsed row, one inside the editor) — receives a freeform command
      and returns generated text that's inserted into the TTS message field. */
  onGenerateAiDraft?: (command: string) => Promise<string>;
  /** Optional replacement for the built-in `VoiceRecorder`. When provided, this component
      is rendered in record mode instead. Used by the Chrome extension to route mic capture
      through an offscreen document (side-panel `getUserMedia` is unreliable). */
  voiceRecorderComponent?: React.ComponentType<VoiceRecorderProps>;
};

// ============================================================================
// VoiceNoteSelectModeContent
// ============================================================================

type VoiceNoteSelectModeContentProps = {
  ttsMessage: string;
  setTtsMessage: (value: string) => void;
  selectedFileId: string | null;
  setSelectedFileId: (value: string | null) => void;
  playingPreviewClipId: string | null;
  setPlayingPreviewClipId: (value: string | null) => void;
  audioDropFiles: AudioDropFileData[];
  onSelectFile: (fileId: string) => void;
  audioMode: 'select' | 'record';
  disabled: boolean;
  loadingText: string;
  text: string;
  previewAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
};

function VoiceNoteSelectModeContent({
  ttsMessage,
  setTtsMessage,
  selectedFileId,
  setSelectedFileId,
  playingPreviewClipId,
  setPlayingPreviewClipId,
  audioDropFiles,
  onSelectFile: _onSelectFile,
  audioMode,
  disabled,
  loadingText: _loadingText,
  text: _text,
  previewAudioRef,
}: VoiceNoteSelectModeContentProps) {
  // Show record UI when in record mode
  if (audioMode === 'record') {
    return (
      <div className="flex flex-col items-center justify-center p-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg">
        <Media.Microphone01 className="size-10" />
        <span className="text-sm mt-2">Click to record</span>
      </div>
    );
  }
  // Show TTS UI when in select mode
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Textarea
        value={ttsMessage}
        onChange={(e) => setTtsMessage(e.target.value)}
        placeholder="Type your message here..."
        rows={4}
        bordered={false}
        color="blue" tone="outline"
        shape="square"
        className="w-full"
      />
      <SeparatorWithPlusIcon />
      <div className="grid grid-rows-[1fr] opacity-100 overflow-hidden">
        <div className="overflow-hidden flex items-center">
          {audioDropFiles.length > 0 ? (
            <FilterSelect
              className="flex-1 min-w-0"
              color="blue" tone="outline"
              shape="square"
              bordered={false}
              icon={<Media.Microphone01 />}
              placeholder="Select a voice clip to append..."
              options={audioDropFiles.map((clip) => ({
                value: clip.id,
                label: clip.name,
                action: {
                  icon:
                    playingPreviewClipId === clip.id ? (
                      <Media.PauseCircle className="size-5" />
                    ) : (
                      <Media.PlayCircle className="size-5" />
                    ),
                  onClick: (value: string) => {
                    const clipToPlay = audioDropFiles.find((c) => c.id === value);
                    if (!clipToPlay?.url) return;

                    if (playingPreviewClipId === value) {
                      previewAudioRef.current?.pause();
                      setPlayingPreviewClipId(null);
                    } else {
                      previewAudioRef.current?.pause();
                      const audio = new Audio(clipToPlay.url);
                      previewAudioRef.current = audio;
                      audio.onended = () => setPlayingPreviewClipId(null);
                      audio.onerror = () => setPlayingPreviewClipId(null);
                      void audio.play().catch(() => {});
                      setPlayingPreviewClipId(value);
                    }
                  },
                  className: cn(
                    'size-7 rounded-full transition-colors flex items-center justify-center',
                    playingPreviewClipId === clip.id
                      ? 'bg-indigo-500 text-white'
                      : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-400 dark:hover:bg-indigo-800',
                  ),
                },
              }))}
              value={selectedFileId ?? ''}
              onChange={(value) => {
                setSelectedFileId(value);
                previewAudioRef.current?.pause();
                setPlayingPreviewClipId(null);
              }}
              disabled={disabled}
            />
          ) : (
            <p className="text-sm text-indigo-600 dark:text-indigo-400 flex-1">No voice notes uploaded yet.</p>
          )}
          {selectedFileId && (
            <Button
              size="sm"
              color="gray"
              text
              data-testid="remove-appended-voice-clip"
              tooltip="Remove appended voice clip"
              disabled={disabled}
              onClick={() => {
                setSelectedFileId(null);
                previewAudioRef.current?.pause();
                previewAudioRef.current = null;
                setPlayingPreviewClipId(null);
              }}
              icon={<General.XMark className="size-4" />}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// VoiceNoteActionCardPure - Main Component
// ============================================================================

export function VoiceNoteActionCardPure({
  audioDropFiles,
  audioDropFilesIsLoading,
  onSendAudio,
  onGenerateVoiceNote,
  onApproveVoiceNote,
  onAudioGenerated,
  onRecordTrigger,
  onRecordingComplete,
  isUploadingRecording,
  onGenerateAiDraft,
  voiceRecorderComponent: RecorderComponent = VoiceRecorder,
}: VoiceNoteActionCardPureProps) {
  const [editing, setEditing] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [isAiDrafting, setIsAiDrafting] = React.useState(false);
  const [selectedFileId, setSelectedFileId] = React.useState<string | null>(null);
  const [playingPreviewClipId, setPlayingPreviewClipId] = React.useState<string | null>(null);
  const [ttsMessageRaw, setTtsMessageRaw] = React.useState('');
  // Defensive: any value entering the TTS textarea that looks like HTML (`<br>`, `<p>`, etc.)
  // gets stripped to plain text. Catches paths where the AI/cadence layer returns TipTap HTML
  // and would otherwise render literal `<br><br>` tags as characters in the textarea.
  const ttsMessage = ttsMessageRaw;
  const setTtsMessage = React.useCallback((value: string) => {
    if (/<\w+[^>]*>|&\w+;/.test(value)) {
      setTtsMessageRaw(getTextFromHtml(value));
      return;
    }
    setTtsMessageRaw(value);
  }, []);
  const [audioMode, setAudioMode] = React.useState<'select' | 'record'>('select');
  const [voiceNoteStepId, setVoiceNoteStepId] = React.useState<string | null>(null);
  const [generatedAudioUrl, setGeneratedAudioUrl] = React.useState<string | null>(null);
  const [generatedAudioId, setGeneratedAudioId] = React.useState<string | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = React.useState<string | null>(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = React.useState<{ blob: Blob; durationMs: number } | null>(null);
  const previewAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const [hasPlayedGeneratedAudio, setHasPlayedGeneratedAudio] = React.useState(false);

  const needsToPlayAudio = !!generatedAudioId && !hasPlayedGeneratedAudio;

  const sendAudio = async (fileId: string) => {
    setIsSending(true);
    try {
      await onSendAudio(fileId);
      setEditing(false);
      setIsSending(false);
    } catch {
      setIsSending(false);
    }
  };

  // AI-draft the TTS message. Uses an empty command by default ("just write something good")
  // — the consumer's `onGenerateAiDraft` is responsible for crafting the prompt with contact
  // context. The draft comes back as TipTap HTML; we strip to plain text (with <br> → \n)
  // before inserting because the textarea is plain-text and would otherwise render literal
  // <br><br> tags as characters.
  const handleAiDraft = async () => {
    if (!onGenerateAiDraft) return;
    setIsAiDrafting(true);
    try {
      const draft = await onGenerateAiDraft('');
      if (draft) setTtsMessage(getTextFromHtml(draft));
    } finally {
      setIsAiDrafting(false);
    }
  };

  const handleGenerateAudio = async () => {
    setIsGenerating(true);
    // Stop any currently-playing preview before regenerating — otherwise the
    // previous draft keeps playing while the new one is being fetched and the
    // user hears the old voice + old appended clip on top of the new one.
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPlayingPreviewClipId(null);
    setHasPlayedGeneratedAudio(false);
    try {
      const voiceDropId = selectedFileId ?? undefined;
      const result = await onGenerateVoiceNote?.(ttsMessage, voiceDropId);
      if (result?.audioUrl) {
        setVoiceNoteStepId(result.stepId);
        setGeneratedAudioUrl(result.audioUrl);
        setGeneratedAudioId(result.audioId);
        // Pass the fresh URL directly. State updates are async, so reading
        // `generatedAudioUrl` from inside `playGeneratedAudio` would still
        // see the previous draft and replay stale audio.
        playGeneratedAudio(result.audioUrl);
      }
      setIsGenerating(false);
    } catch {
      setIsGenerating(false);
    }
  };

  const handleApproveVoiceNote = async () => {
    if (!voiceNoteStepId) return;
    setIsSending(true);
    try {
      await onApproveVoiceNote?.(voiceNoteStepId);
      setEditing(false);
      setIsSending(false);
      setVoiceNoteStepId(null);
    } catch {
      setIsSending(false);
    }
  };

  // Play the generated audio + selected clip sequentially.
  // Accepts an optional override URL so callers that just regenerated a draft
  // can pass the fresh URL directly without waiting for state to flush.
  const playGeneratedAudio = (urlOverride?: string) => {
    const url = urlOverride ?? generatedAudioUrl;
    if (!url) return;

    if (playingPreviewClipId === 'generated' && !urlOverride) {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
      setPlayingPreviewClipId(null);
      setHasPlayedGeneratedAudio(false);
    } else {
      previewAudioRef.current?.pause();
      const audio = new Audio(url);
      previewAudioRef.current = audio;

      // When generated audio ends, mark as played and chain to the voice clip if selected
      audio.onended = () => {
        setHasPlayedGeneratedAudio(true);
        onAudioGenerated?.(url, generatedAudioId ?? '');
        const selectedClip = audioDropFiles.find((f) => f.id === selectedFileId);
        if (selectedClip?.url) {
          const clipAudio = new Audio(selectedClip.url);
          previewAudioRef.current = clipAudio;
          clipAudio.onended = () => setPlayingPreviewClipId(null);
          clipAudio.onerror = () => setPlayingPreviewClipId(null);
          void clipAudio.play().catch(() => {});
        } else {
          setPlayingPreviewClipId(null);
        }
      };
      audio.onerror = () => {
        setPlayingPreviewClipId(null);
        setHasPlayedGeneratedAudio(false);
      };
      void audio.play().catch(() => {});
      setPlayingPreviewClipId('generated');
    }
  };

  const canPlayGenerated = generatedAudioUrl && (selectedFileId || generatedAudioId);

  // Auto-select newly generated audio when audioDropFiles changes (only if no file is selected)
  const previousFileCount = React.useRef(audioDropFiles.length);
  React.useEffect(() => {
    if (audioDropFiles.length > previousFileCount.current && audioDropFiles.length > 0) {
      // New file was added - select the most recent one, but only if user hasn't selected one
      const newFile = audioDropFiles[audioDropFiles.length - 1];
      if (newFile && !selectedFileId) {
        setSelectedFileId(newFile.id);
      }
    }
    previousFileCount.current = audioDropFiles.length;
  }, [audioDropFiles, selectedFileId]);

  // Reset hasPlayedGeneratedAudio when message changes so user must re-listen
  const previousTtsMessage = React.useRef(ttsMessage);
  React.useEffect(() => {
    if (ttsMessage !== previousTtsMessage.current) {
      setHasPlayedGeneratedAudio(false);
      previousTtsMessage.current = ttsMessage;
    }
  }, [ttsMessage]);

  // Reset hasPlayedGeneratedAudio when clip changes so user must re-listen
  const previousSelectedFileId = React.useRef(selectedFileId);
  React.useEffect(() => {
    if (selectedFileId !== previousSelectedFileId.current) {
      setHasPlayedGeneratedAudio(false);
      previousSelectedFileId.current = selectedFileId;
    }
  }, [selectedFileId]);

  if (!editing) {
    return (
      <div className="flex gap-1.5 flex-1">
        <Button
          className="flex-1"
          onClick={() => setEditing(true)}
          icon={<StepIconSvg iconType="voice_note" />}
          color="blue" tone="soft">
          <span>
            Send a{' '}
            <span className="font-medium text-blue-700 underline decoration-blue-300/50 underline-offset-2 dark:text-blue-300 dark:decoration-blue-500/50">
              Voice Note
            </span>
          </span>
        </Button>
        {/* Magic-wand AI generate button — mirrors the ActionCardPure pattern used by the
            sibling LinkedIn DM / Email rows so users see a consistent affordance. Expands the
            card and immediately drafts a TTS message via AI. */}
        {onGenerateAiDraft && (
          <Button
            tooltip="Draft with AI"
            color="blue" tone="soft"
            disabled={isAiDrafting}
            onClick={() => {
              setEditing(true);
              setAudioMode('select');
              void handleAiDraft();
            }}
            icon={isAiDrafting ? <WaveSpinner /> : <Editor.Wand />}
          />
        )}
        {onRecordTrigger && (
          <Button
            tooltip="Record Audio Note"
            color="red" tone="soft"
            onClick={() => {
              onRecordTrigger?.();
              setEditing(true);
              setAudioMode('record');
            }}
            icon={<Media.Microphone01 />}
          />
        )}
      </div>
    );
  }

  const onCancel = () => {
    // Stop any preview that's still playing so closing the card doesn't leave
    // audio playing in the background.
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPlayingPreviewClipId(null);
    setHasPlayedGeneratedAudio(false);
    setEditing(false);
    setAudioMode('select');
    // Clean up recorded audio
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
      setRecordedAudioUrl(null);
      setRecordedAudioBlob(null);
    }
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-col border rounded-lg overflow-hidden',
          audioMode === 'select' && 'border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-950',
          audioMode === 'record' && 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-950',
        )}>
        <div className="flex items-center gap-2 px-3 py-2">
          <StepIconSvg iconType="voice_note" className="size-5 text-zumthor-600 shrink-0" />
          <span className="flex-1 text-sm font-medium text-astronaut-700 dark:text-astronaut-300">
            {audioMode === 'record' && 'Record a voice note'}
            {audioMode === 'select' && 'Send a voice note'}
          </span>

          {/* Mode selection buttons */}
          {onRecordingComplete && (
            <Button
              size="sm"
              color="red" tone={audioMode === 'record' ? 'filled' : 'soft'}
              bordered={false}
              tooltip="Record new"
              onClick={() => setAudioMode('record')}
              icon={<Media.Microphone01 className="size-4" />}
            />
          )}

          <Button
            size="sm"
            text
            color="gray"
            tooltip="Cancel"
            onClick={onCancel}
            disabled={isGenerating || isSending}
            icon={<General.XMark className="size-4" />}
          />
        </div>
        {audioDropFilesIsLoading && (
          <div className="flex items-center justify-center p-4">
            <WaveSpinner />
          </div>
        )}
        {!audioDropFilesIsLoading && audioMode === 'select' && (
          <VoiceNoteSelectModeContent
            ttsMessage={ttsMessage}
            setTtsMessage={setTtsMessage}
            selectedFileId={selectedFileId}
            setSelectedFileId={setSelectedFileId}
            playingPreviewClipId={playingPreviewClipId}
            setPlayingPreviewClipId={setPlayingPreviewClipId}
            audioDropFiles={audioDropFiles}
            onSelectFile={sendAudio}
            disabled={isGenerating || isSending}
            loadingText="Sending Voice Note..."
            text="Send Voice Note"
            previewAudioRef={previewAudioRef}
            audioMode={audioMode}
          />
        )}
        {/* Record mode - voice recorder */}
        {!audioDropFilesIsLoading && audioMode === 'record' && (
          <div className="p-3">
            {!recordedAudioUrl && (
              <RecorderComponent
                onRecordingComplete={(audioBlob, durationMs) => {
                  // Store locally for preview instead of immediately uploading
                  const url = URL.createObjectURL(audioBlob);
                  setRecordedAudioUrl(url);
                  setRecordedAudioBlob({ blob: audioBlob, durationMs });
                }}
                // Cancel releases the mic (the recorder handles teardown) and drops back to the
                // message view instead of leaving the user stuck in record mode with a live mic.
                onCancel={() => setAudioMode('select')}
                isUploading={isUploadingRecording}
                disabled={isUploadingRecording}
                autoStart
              />
            )}
            {recordedAudioUrl && (
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <Media.Microphone01 className="size-5" />
                  <span>Recording ready</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    color="red" tone="soft"
                    size="sm"
                    onClick={() => {
                      if (playingPreviewClipId === 'recorded') {
                        previewAudioRef.current?.pause();
                        setPlayingPreviewClipId(null);
                      } else {
                        previewAudioRef.current?.pause();
                        const audio = new Audio(recordedAudioUrl);
                        previewAudioRef.current = audio;
                        audio.onended = () => setPlayingPreviewClipId(null);
                        audio.onerror = () => setPlayingPreviewClipId(null);
                        void audio.play().catch(() => {});
                        setPlayingPreviewClipId('recorded');
                      }
                    }}
                    icon={playingPreviewClipId === 'recorded' ? <Media.PauseCircle /> : <Media.PlayCircle />}>
                    {playingPreviewClipId === 'recorded' ? 'Pause' : 'Play'}
                  </Button>
                  <Button
                    color="red"
                    size="sm"
                    onClick={() => {
                      // Re-record
                      if (recordedAudioUrl) {
                        URL.revokeObjectURL(recordedAudioUrl);
                      }
                      setRecordedAudioUrl(null);
                      setRecordedAudioBlob(null);
                      setPlayingPreviewClipId(null);
                    }}
                    icon={<Media.Microphone01 />}>
                    Re-record
                  </Button>
                  <Button
                    color="green"
                    size="sm"
                    disabled={isUploadingRecording}
                    onClick={() => {
                      if (recordedAudioBlob) {
                        onRecordingComplete?.(recordedAudioBlob.blob, recordedAudioBlob.durationMs);
                      }
                    }}
                    icon={<General.Check />}>
                    {isUploadingRecording ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Buttons below the bordered section - only show for select/generate mode */}
      {audioMode === 'select' && (
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleGenerateAudio}
          disabled={isGenerating || isSending || !ttsMessage.trim()}
          className="flex-1 bg-indigo-600! text-white! hover:bg-indigo-700! active:bg-indigo-800! dark:bg-indigo-700! dark:hover:bg-indigo-800!"
          tooltip={
            voiceNoteStepId
              ? "Regenerate the audio. This updates the same draft — it doesn't create a duplicate send."
              : 'Generate audio for this voice note message'
          }>
          {isGenerating
            ? voiceNoteStepId
              ? 'Updating draft…'
              : 'Generating…'
            : voiceNoteStepId
              ? 'Update Draft'
              : 'Generate Audio'}
        </Button>
        {/* Play button when needs to listen first - like todo does */}
        {needsToPlayAudio && (
          <Button
            color="yellow"
            size="sm"
            onClick={() => playGeneratedAudio()}
            disabled={isGenerating || isSending}
            icon={playingPreviewClipId === 'generated' ? <Media.PauseCircle /> : <Media.PlayCircle />}
            tooltip="Listen to audio before sending">
            Listen
          </Button>
        )}
        {/* Play button for already played generated audio */}
        {!needsToPlayAudio && canPlayGenerated && (
          <Button
            color="blue" tone="soft"
            size="sm"
            onClick={() => playGeneratedAudio()}
            disabled={isGenerating || isSending}
            icon={playingPreviewClipId === 'generated' ? <Media.PauseCircle /> : <Media.PlayCircle />}
          />
        )}
        {/* Send/Schedule button - schedules the step for execution */}
        {voiceNoteStepId && (
          <Button
            color="yellow"
            size="sm"
            onClick={handleApproveVoiceNote}
            disabled={isGenerating || isSending || needsToPlayAudio}
            className="flex-1">
            {isSending ? 'Scheduling...' : needsToPlayAudio ? 'Listen first' : 'Schedule Voice Note'}
          </Button>
        )}
        {/* Play button for pre-recorded clips in dropdown */}
        {selectedFileId && !generatedAudioId && (
          <Button
            color="blue" tone="soft"
            size="sm"
            onClick={() => {
              const clip = audioDropFiles.find(f => f.id === selectedFileId);
              if (clip?.url) {
                if (playingPreviewClipId === selectedFileId) {
                  previewAudioRef.current?.pause();
                  setPlayingPreviewClipId(null);
                } else {
                  previewAudioRef.current?.pause();
                  const audio = new Audio(clip.url);
                  previewAudioRef.current = audio;
                  audio.onended = () => setPlayingPreviewClipId(null);
                  audio.onerror = () => setPlayingPreviewClipId(null);
                  void audio.play().catch(() => {});
                  setPlayingPreviewClipId(selectedFileId);
                }
              }
            }}
            disabled={isGenerating || isSending}
            icon={playingPreviewClipId === selectedFileId ? <Media.PauseCircle /> : <Media.PlayCircle />}
          />
        )}
      </div>
      )}
    </>
  );
}
